"use client";

import * as React from "react";
import { useIsTouchDevice } from "../../hooks/use-is-touch-device";

/**
 * A transparent, viewport-covering layer seated in a Base UI portal BESIDE
 * the positioner, before it (a positioner is transformed, so a fixed child
 * of it would be contained to its box), on a coarse pointer only. It is the backdrop Base UI's
 * own `modal` mounts for a popover, a menu or a select, hand-built for the
 * primitives that expose no `modal` switch (the preview card): the tap that
 * closes the popup is spent here and never lands on what sat beneath the
 * finger. `data-base-ui-inert` is what tells floating-ui's outside-press
 * detection the layer existed when the popup rendered, so the press still
 * reads as an outside press and dismisses.
 *
 * `cutout` keeps one element tappable through the layer — the surface that
 * drives the popup (the module rail under its flyout), which a press must
 * reach as a press and not as a dismissal. A mouse gets no layer.
 */
export function TouchDismissLayer({
  cutout,
}: {
  /** The element (or a getter for it) the layer leaves uncovered. */
  cutout?: Element | null | (() => Element | null) | undefined;
}): React.ReactElement | null {
  const touch = useIsTouchDevice();
  if (!touch) return null;
  const el = typeof cutout === "function" ? cutout() : cutout;
  let clipPath: string | undefined;
  if (el) {
    const r = el.getBoundingClientRect();
    clipPath = `polygon(0% 0%,100% 0%,100% 100%,0% 100%,0% 0%,${r.left}px ${r.top}px,${r.left}px ${r.bottom}px,${r.right}px ${r.bottom}px,${r.right}px ${r.top}px,${r.left}px ${r.top}px)`;
  }
  return (
    <div
      aria-hidden
      data-base-ui-inert=""
      data-slot="touch-dismiss-layer"
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        // The positioner's own rung; the popup follows in DOM order and so
        // paints above the layer.
        zIndex: 50,
        userSelect: "none",
        WebkitUserSelect: "none",
        clipPath,
      }}
    />
  );
}
